import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { bulkUpdateOrdiniStatus, bulkUpdateOrdiniFields, getCorrieri, getBanche } from '../services/api';
import toast from 'react-hot-toast'; // Import toast
import MotivoRimborsoFields, { motivoRimborsoCompleto } from './MotivoRimborsoFields';
import { METODI_PAGAMENTO } from './metodiPagamento';

const BulkEditOrdineModal = ({ show, onHide, selectedIds, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');

  const statiHardcoded = [
    "Da Evadere",
    "Confermato",
    "Parz.Concluso",
    "Concluso",
    "Annullato",
    "Rimborsato",
    "Non Ritirato - Contrassegno",
  ];

  const [newStatus, setNewStatus] = useState('');
  const [motivo, setMotivo] = useState({ motivo_rimborso: '', note_rimborso: '' });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [corriereId, setCorriereId] = useState('');
  const [bancaId, setBancaId] = useState('');
  const [note, setNote] = useState('');
  const [noteMode, setNoteMode] = useState('aggiungi');
  const [corrieri, setCorrieri] = useState([]);
  const [banche, setBanche] = useState([]);

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccess(false);
      setNewStatus(''); // Reset values on show
      setMotivo({ motivo_rimborso: '', note_rimborso: '' });
      setPaymentMethod('');
      setCorriereId('');
      setBancaId('');
      setNote('');
      setNoteMode('aggiungi');
      setProcessingStatus('');
      Promise.all([getCorrieri(0, 100000), getBanche(0, 100000)])
        .then(([corrieriRes, bancheRes]) => {
          setCorrieri(corrieriRes.data.corrieri);
          setBanche(bancheRes.data.banche);
        })
        .catch(() => toast.error("Impossibile caricare corrieri e banche."));
    }
  }, [show]);

  const haCampi = !!(paymentMethod || corriereId || bancaId || note.trim());
  const nulla = !newStatus && !haCampi;

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.error("Nessun ordine selezionato per la modifica.");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setProcessingStatus(`Aggiornamento di ${selectedIds.length} ordini in corso...`);

    try {
      // Prima i campi semplici, poi lo stato (che puo' registrare l'ordine su Firefly con i dati gia' corretti)
      let saltati = [];
      if (haCampi) {
        const fields = {};
        if (paymentMethod) fields.payment_method = paymentMethod;
        if (corriereId) fields.corriere_id = Number(corriereId);
        if (bancaId) fields.banca_id = Number(bancaId);
        if (note.trim()) {
          fields.note = note.trim();
          fields.note_mode = noteMode;
        }
        const res = await bulkUpdateOrdiniFields(selectedIds, fields);
        saltati = res.data.saltati || [];
        const nonRegistrati = res.data.non_registrati_firefly || [];
        if ((res.data.registrati_firefly || []).length > 0) {
          toast.success(`${res.data.registrati_firefly.length} ordini conclusi registrati su Firefly.`);
        }
        if (nonRegistrati.length > 0) {
          toast.error(`Rapporto assegnato ma NON registrati su Firefly: #${nonRegistrati.join(', #')}. Controlla il rapporto o riapri e risalva l'ordine.`, { duration: 12000 });
        }
      }
      if (newStatus) {
        await bulkUpdateOrdiniStatus(selectedIds, newStatus, newStatus === 'Rimborsato' ? motivo : null);
      }

      if (saltati.length > 0) {
        toast(`${saltati.length} ordini saltati (pagamento/corriere/banca non modificabili perché già registrati su Firefly): #${saltati.map(s => s.id).join(', #')}`, { duration: 10000 });
      }
      setProcessingStatus(`✓ Completato! ${selectedIds.length - saltati.length} ordini aggiornati con successo.`);
      setSuccess(true);
      setTimeout(() => {
        onHide();
        onSuccess();
      }, saltati.length > 0 ? 3000 : 1500);
    } catch (err) {
      console.error("Errore durante la modifica massiva degli ordini:", err);
      let errorMessage = "Si è verificato un errore durante l'aggiornamento massivo.";
      if (err.response && err.response.data && err.response.data.detail) {
        errorMessage += "\nDettagli: " + (typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail));
      }
      setError(errorMessage);
      setProcessingStatus('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Modifica Massiva Ordini ({selectedIds.length} selezionati)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">Ordini aggiornati con successo!</Alert>}
        {loading && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">{processingStatus}</small>
            </div>
            <ProgressBar animated now={100} variant="primary" className="mb-2" />
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <small className="text-muted">Elaborazione in corso, attendere...</small>
            </div>
          </div>
        )}
        <Form>
          <Form.Group controlId="formNewStatus" className="mb-3">
            <Form.Label>Nuovo Stato</Form.Label>
            <Form.Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} disabled={loading}>
              <option value="">Seleziona un nuovo stato</option>
              {statiHardcoded.map(stato => (
                <option key={stato} value={stato}>{stato}</option>
              ))}
            </Form.Select>
            {newStatus === 'Annullato' && (
              <Form.Text className="text-muted">Merce integra: lo stock rientra a magazzino e l'offerta su IBS viene aggiornata.</Form.Text>
            )}
            {newStatus === 'Non Ritirato - Contrassegno' && (
              <Form.Text className="text-muted">Contrassegno non ritirato: lo stock rientra, le transazioni Firefly vengono stornate e il contrassegno viene disabilitato al cliente.</Form.Text>
            )}
            {newStatus === 'Rimborsato' && (
              <Form.Text className="text-muted">Acquirente rimborsato per pezzo difettoso, perso o respinto: lo stock NON rientra a magazzino.</Form.Text>
            )}
          </Form.Group>
          {newStatus === 'Rimborsato' && (
            <>
              <Form.Text className="d-block text-danger mb-2">
                Per gli ordini marketplace IBS il rimborso al cliente parte automaticamente anche su Mirakl (importo residuo rimborsabile) e i reclami aperti vengono chiusi. Non si può annullare.
              </Form.Text>
              <MotivoRimborsoFields value={motivo} onChange={setMotivo} disabled={loading} />
            </>
          )}
          <hr />
          <Form.Group controlId="formPaymentMethod" className="mb-3">
            <Form.Label>Metodo di pagamento</Form.Label>
            <Form.Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} disabled={loading}>
              <option value="">Non modificare</option>
              {METODI_PAGAMENTO.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">Come ha pagato il cliente. Il rapporto di incasso (la banca) è un campo a parte.</Form.Text>
          </Form.Group>
          <Form.Group controlId="formCorriere" className="mb-3">
            <Form.Label>Corriere</Form.Label>
            <Form.Select value={corriereId} onChange={(e) => setCorriereId(e.target.value)} disabled={loading}>
              <option value="">Non modificare</option>
              {corrieri.map(c => (
                <option key={c.id} value={c.id}>{c.denominazione}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="formBanca" className="mb-3">
            <Form.Label>Banca</Form.Label>
            <Form.Select value={bancaId} onChange={(e) => setBancaId(e.target.value)} disabled={loading}>
              <option value="">Non modificare</option>
              {banche.map(b => (
                <option key={b.id} value={b.id}>{b.denominazione}</option>
              ))}
            </Form.Select>
            {paymentMethod && !bancaId && (
              <Form.Text className="text-muted d-block">Se non scegli una banca, il rapporto di incasso viene proposto dal metodo (contanti → CONTANTI, carta/POS → MY POS, PayPal, SumUp, contrassegno, marketplace), solo se l'ordine non ne ha già uno. Per il bonifico del negozio scegli tu la banca.</Form.Text>
            )}
            {(paymentMethod || corriereId || bancaId) && (
              <Form.Text className="text-muted">Pagamento, corriere e banca non vengono modificati sugli ordini già registrati su Firefly: quegli ordini vengono saltati.</Form.Text>
            )}
          </Form.Group>
          <Form.Group controlId="formNote" className="mb-3">
            <Form.Label>Note</Form.Label>
            <Form.Control as="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Lascia vuoto per non modificare" disabled={loading} />
            {note.trim() && (
              <div className="mt-2">
                <Form.Check inline type="radio" id="noteModeAggiungi" label="Aggiungi in coda" checked={noteMode === 'aggiungi'} onChange={() => setNoteMode('aggiungi')} disabled={loading} />
                <Form.Check inline type="radio" id="noteModeSostituisci" label="Sostituisci" checked={noteMode === 'sostituisci'} onChange={() => setNoteMode('sostituisci')} disabled={loading} />
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Annulla
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading || nulla || (newStatus === 'Rimborsato' && !motivoRimborsoCompleto(motivo))}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Salva Modifiche'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkEditOrdineModal;
